// Lightweight i18n. Browser default is detected from navigator.language; user
// override is stored in localStorage. The frontend imports t() and calls
// applyI18n() after each render.

export type Lang = 'en' | 'zh';

const STORAGE_KEY = 'wcl-warrior-analyzer.lang';

let currentLang: Lang = 'en';
const listeners = new Set<() => void>();

interface Dict { [key: string]: string }

// English source. Keys are dot-separated paths.
const EN: Dict = {
  // App-level
  'app.title': 'RUOK Guild · WCL Warrior Analyzer',
  'app.subtitle': 'Compare a Naxx fury warrior pull against a wowsims/classic simulation of the same gear and talents.',
  'app.author': 'by 伟大光荣正确的 LEONIDASI',
  'app.backend': 'Backend',
  'app.lang.en': 'English',
  'app.lang.zh': '中文',

  // Loader
  'loader.heading': 'Load a report',
  'loader.input.label': 'WCL report URL or code',
  'loader.input.placeholder': 'https://vanilla.warcraftlogs.com/reports/aBcDeFgH or aBcDeFgH',
  'loader.button': 'Load',
  'loader.loading': 'Loading report from WCL...',
  'loader.networkError': 'Network error: {msg}',

  // Report
  'report.fights': 'Naxx boss fights',
  'report.warriors': 'Warriors in this raid',
  'report.warriors.compare': 'Compare with…',
  'report.totalLength': 'total log length',
  'report.zone': 'Zone',
  'report.zoneUnknown': 'unknown',
  'report.fight.kill': '✓ kill',
  'report.fight.wipe': '✗ wipe',
  'report.fight.players': '{n} players',
  'report.empty.fights': 'No Naxxramas boss fights found in this report.',
  'report.empty.warriors': 'No warriors in this report.',
  'report.loaded': 'Loaded "{title}" — {fights} Naxx fights, {warriors} warriors.',

  // Player snapshot
  'player.heading.snapshot': 'Player snapshot:',
  'player.heading.on': 'on',
  'player.fetching': 'Fetching CombatantInfo from WCL...',
  'player.parsed': 'Parsed · fight {dur} · {result} · spec {spec}',
  'player.noEvents': 'No CombatantInfo events for this player in this fight. Likely the player joined mid-fight, or Advanced Combat Logging was off when the log was recorded.',
  'player.result.kill': 'kill',
  'player.result.wipe': 'wipe',

  // Panels
  'panel.overview': 'Overview',
  'panel.gear': 'Gear (19 slots)',
  'panel.auras': 'Buffs & consumables',
  'aura.group.assumed': 'Raid-given buffs (assumed — vanilla log limitation)',
  'assumed.heading': 'Assume these raid buffs were on you',
  'assumed.help': 'Vanilla combat logs only record raid buffs cast in combat. Pre-pull blessings/MotW/Fortitude are invisible to WCL. Pick your faction and tick what you actually had.',
  'assumed.faction': 'Faction',
  'assumed.faction.alliance': 'Alliance',
  'assumed.faction.horde': 'Horde',
  'assumed.faction.auto': 'Auto-detect',
  'assumed.detected.alliance': 'auto → Alliance (raid has Paladin)',
  'assumed.detected.horde': 'auto → Horde (raid has Shaman)',
  'assumed.detected.unknown': 'auto → unknown (no Paladin/Shaman in raid)',
  'panel.talents': 'Talents',
  'panel.talents.hint': '— left-click adds, right-click removes',
  'panel.talents.openWowhead': 'Open in Wowhead calculator →',
  'panel.talents.reset': 'Reset',
  'panel.talents.resetTip': 'Reset to the inferred build',
  'panel.raw.summary': 'Show raw CombatantInfo JSON',

  // Overview / stats
  'overview.spec': 'Spec',
  'overview.treePoints': '(tree points: {arms} Arms / {fury} Fury / {prot} Prot)',
  'overview.note': "WCL's vanilla schema doesn't report melee crit / haste — those are derived from agility, gear, and talents at sim time.",
  'stat.strength': 'Strength',
  'stat.agility': 'Agility',
  'stat.stamina': 'Stamina',
  'stat.intellect': 'Intellect',
  'stat.spirit': 'Spirit',
  'stat.armor': 'Armor',
  'stat.ap': 'AP',
  'stat.crit': 'Crit %',
  'stat.allAttrs': 'all stats',
  'assumed.group.raid': 'Raid buffs',
  'assumed.group.world': 'World buffs',
  'assumed.group.consumable': 'Consumables',
  'assumed.group.food': 'Food (well fed)',
  'assumed.group.self': 'Self buffs',
  'stat.crit.berserker': '(Berserker +3%)',
  'stat.crit.battle': '(Battle/Defensive)',
  'overview.charSheetNote.base': "Base view: race+class baseline + gear stats summed from item tooltips. Matches the in-town character sheet (no raid buffs / consumables).",
  'overview.charSheetNote.buffed': "Buffed view: WCL CombatantInfo values captured at fight start, with raid buffs / flasks / food / world buffs already applied. Matches the in-fight character sheet.",
  'overview.mode.base': 'Base (no buffs)',
  'overview.mode.buffed': 'In-fight (buffed)',
  'overview.breakdown.summary': 'Show AP / Crit breakdown (where each value comes from)',

  // Crit breakdown labels (hover tooltip + expandable details). Self-computed
  // from gear+enchants+talents; no WCL crit data is consulted.
  'crit.bd.classBase': 'Base',
  'crit.bd.agility': 'Agi',
  'crit.bd.cruelty': 'Cruelty',
  'crit.bd.stance': 'Stance',
  'crit.bd.stance.berserker': '(Berserker)',
  'crit.bd.stance.none': '(none)',
  'crit.bd.gear': 'Gear+ench',
  'crit.bd.gear.note': 'parsed from item tooltips and enchant spell descriptions',
  'crit.bd.buffs': 'Buffs',
  'crit.bd.buffs.note': 'crit % from selected world / consumable / food buffs',
  'crit.bd.total': 'Total',

  // AP breakdown labels (same model as crit).
  'ap.bd.classBase': 'Base',
  'ap.bd.classBase.note': 'level × 3 − 20',
  'ap.bd.strength': 'Str',
  'ap.bd.gear': 'Gear+ench',
  'ap.bd.gear.note': 'flat AP from items + enchants',
  'ap.bd.buffs': 'Buffs',
  'ap.bd.buffs.note': 'flat AP from Battle Shout / BoM / Trueshot / Juju Might / etc.',
  'ap.bd.total': 'Total',

  // Spec
  'spec.fury': 'FURY',
  'spec.arms': 'ARMS',
  'spec.protection': 'PROTECTION',

  // Gear table
  'gear.col.slot': 'Slot',
  'gear.col.item': 'Item',
  'gear.col.ilvl': 'iLvl',
  'gear.col.enchants': 'Enchants',
  'gear.empty': '(empty)',
  'gear.enchant.permanent': 'enchant {id}',
  'gear.enchant.temporary': 'temp {id}',
  'gear.set': 'set {id}',
  'gear.itemPrefix': 'item',

  // Slots
  'slot.Head': 'Head',
  'slot.Neck': 'Neck',
  'slot.Shoulder': 'Shoulder',
  'slot.Shirt': 'Shirt',
  'slot.Chest': 'Chest',
  'slot.Waist': 'Waist',
  'slot.Legs': 'Legs',
  'slot.Feet': 'Feet',
  'slot.Wrist': 'Wrist',
  'slot.Hands': 'Hands',
  'slot.Finger 1': 'Finger 1',
  'slot.Finger 2': 'Finger 2',
  'slot.Trinket 1': 'Trinket 1',
  'slot.Trinket 2': 'Trinket 2',
  'slot.Back': 'Back',
  'slot.Main Hand': 'Main Hand',
  'slot.Off Hand': 'Off Hand',
  'slot.Ranged': 'Ranged',
  'slot.Tabard': 'Tabard',

  // Talents
  'talents.allocated': 'Allocated',
  'talents.armsLabel': '{n} Arms',
  'talents.furyLabel': '{n} Fury',
  'talents.protLabel': '{n} Prot',
  'talents.total': '(total {cur} / {max})',
  'talents.warn': "⚠ doesn't match WCL tree counts",
  'talents.expected': 'expected {n} pts',
  'talents.tier': 'T{n}',
  'talents.tierTitle': 'row {row} (needs {pts} pts in tree)',
  'talents.tree.Arms': 'Arms',
  'talents.tree.Fury': 'Fury',
  'talents.tree.Protection': 'Protection',

  // Talent names — auto-generated readable form, kept explicit for clarity.
  'talent.improvedHeroicStrike': 'Improved Heroic Strike',
  'talent.deflection': 'Deflection',
  'talent.improvedRend': 'Improved Rend',
  'talent.improvedCharge': 'Improved Charge',
  'talent.tacticalMastery': 'Tactical Mastery',
  'talent.improvedThunderClap': 'Improved Thunder Clap',
  'talent.improvedOverpower': 'Improved Overpower',
  'talent.angerManagement': 'Anger Management',
  'talent.deepWounds': 'Deep Wounds',
  'talent.twoHandedWeaponSpecialization': 'Two-Handed Weapon Specialization',
  'talent.impale': 'Impale',
  'talent.axeSpecialization': 'Axe Specialization',
  'talent.sweepingStrikes': 'Sweeping Strikes',
  'talent.maceSpecialization': 'Mace Specialization',
  'talent.swordSpecialization': 'Sword Specialization',
  'talent.polearmSpecialization': 'Polearm Specialization',
  'talent.improvedHamstring': 'Improved Hamstring',
  'talent.mortalStrike': 'Mortal Strike',
  'talent.boomingVoice': 'Booming Voice',
  'talent.cruelty': 'Cruelty',
  'talent.improvedDemoralizingShout': 'Improved Demoralizing Shout',
  'talent.unbridledWrath': 'Unbridled Wrath',
  'talent.improvedCleave': 'Improved Cleave',
  'talent.piercingHowl': 'Piercing Howl',
  'talent.bloodCraze': 'Blood Craze',
  'talent.improvedBattleShout': 'Improved Battle Shout',
  'talent.dualWieldSpecialization': 'Dual Wield Specialization',
  'talent.improvedExecute': 'Improved Execute',
  'talent.enrage': 'Enrage',
  'talent.improvedSlam': 'Improved Slam',
  'talent.deathWish': 'Death Wish',
  'talent.improvedIntercept': 'Improved Intercept',
  'talent.improvedBerserkerRage': 'Improved Berserker Rage',
  'talent.flurry': 'Flurry',
  'talent.bloodthirst': 'Bloodthirst',
  'talent.shieldSpecialization': 'Shield Specialization',
  'talent.anticipation': 'Anticipation',
  'talent.improvedBloodrage': 'Improved Bloodrage',
  'talent.toughness': 'Toughness',
  'talent.ironWill': 'Iron Will',
  'talent.lastStand': 'Last Stand',
  'talent.improvedShieldBlock': 'Improved Shield Block',
  'talent.improvedRevenge': 'Improved Revenge',
  'talent.defiance': 'Defiance',
  'talent.improvedSunderArmor': 'Improved Sunder Armor',
  'talent.improvedDisarm': 'Improved Disarm',
  'talent.improvedTaunt': 'Improved Taunt',
  'talent.improvedShieldWall': 'Improved Shield Wall',
  'talent.concussionBlow': 'Concussion Blow',
  'talent.improvedShieldBash': 'Improved Shield Bash',
  'talent.oneHandedWeaponSpecialization': 'One-Handed Weapon Specialization',
  'talent.shieldSlam': 'Shield Slam',

  // Common Naxx-era raid buffs by spell id. Falls back to the WCL-provided
  // English name when an id isn't listed here.
  'aura.2458':   'Berserker Stance',
  'aura.11348':  'Greater Armor',
  'aura.22817':  "Fengus' Ferocity",
  'aura.22818':  "Mol'dar's Moxie",
  'aura.22820':  "Slip'kik's Savvy",
  'aura.17038':  'Winterfall Firewater',
  'aura.24799':  'Well Fed',
  'aura.25804':  'Rumsey Rum Black Label',
  'aura.17538':  'Elixir of the Mongoose',
  'aura.26276':  'Greater Firepower',
  'aura.17539':  'Greater Arcane Elixir',
  'aura.16323':  'Juju Power',
  'aura.16329':  'Juju Might',
  'aura.3593':   'Elixir of Fortitude',
  'aura.355363': 'Rallying Cry of the Dragonslayer',
  'aura.355366': "Warchief's Blessing",
  'aura.355365': 'Spirit of Zandalar',
  'aura.15366':  'Songflower Serenade',
  'aura.17626':  'Flask of the Titans',
  'aura.24382':  'Spirit of Zanza',
  'aura.24361':  "Mighty Troll's Blood Elixir",

  // Naxxramas boss names by WCL encounterID (51xxx vanilla / 1xxx classic).
  'boss.51107': "Anub'Rekhan",
  'boss.51110': 'Grand Widow Faerlina',
  'boss.51116': 'Maexxna',
  'boss.51117': 'Noth the Plaguebringer',
  'boss.51112': 'Heigan the Unclean',
  'boss.51115': 'Loatheb',
  'boss.51113': 'Instructor Razuvious',
  'boss.51109': 'Gothik the Harvester',
  'boss.51121': 'The Four Horsemen',
  'boss.51118': 'Patchwerk',
  'boss.51111': 'Grobbulus',
  'boss.51108': 'Gluth',
  'boss.51120': 'Thaddius',
  'boss.51119': 'Sapphiron',
  'boss.51114': "Kel'Thuzad",
  // Classic (WotLK) IDs — same names.
  'boss.1107': "Anub'Rekhan",
  'boss.1110': 'Grand Widow Faerlina',
  'boss.1116': 'Maexxna',
  'boss.1117': 'Noth the Plaguebringer',
  'boss.1112': 'Heigan the Unclean',
  'boss.1115': 'Loatheb',
  'boss.1113': 'Instructor Razuvious',
  'boss.1109': 'Gothik the Harvester',
  'boss.1121': 'The Four Horsemen',
  'boss.1118': 'Patchwerk',
  'boss.1111': 'Grobbulus',
  'boss.1108': 'Gluth',
  'boss.1120': 'Thaddius',
  'boss.1119': 'Sapphiron',
  'boss.1114': "Kel'Thuzad",

  // Group headings for the buff panel
  'aura.group.starting':   'Starting (snapshot)',
  'aura.group.persistent': 'Raid buffs (≥70% uptime)',
  'aura.group.transient':  '{n} transient buffs (procs / cooldowns)',

  // Common raid buffs not always in COMBATANT_INFO
  'aura.25898':  'Greater Blessing of Kings',
  'aura.25899':  'Greater Blessing of Sanctuary',
  'aura.25895':  'Greater Blessing of Might',
  'aura.25894':  'Greater Blessing of Wisdom',
  'aura.25890':  'Greater Blessing of Light',
  'aura.25918':  'Greater Blessing of Salvation',
  'aura.20217':  'Blessing of Kings',
  'aura.19742':  'Blessing of Wisdom',
  'aura.19740':  'Blessing of Might',
  'aura.20911':  'Blessing of Sanctuary',
  'aura.1038':   'Blessing of Salvation',
  'aura.10938':  'Power Word: Fortitude',
  'aura.21564':  'Prayer of Fortitude',
  'aura.27683':  'Prayer of Shadow Protection',
  'aura.10958':  'Shadow Protection',
  'aura.21850':  'Gift of the Wild',
  'aura.9885':   'Mark of the Wild',
  'aura.27007':  'Trueshot Aura',
  'aura.20140':  'Greater Blessing of Wisdom',
  'aura.25289':  'Battle Shout',
  'aura.469':    'Commanding Shout',
  'aura.27149':  'Devotion Aura',
  'aura.27150':  'Retribution Aura',
  'aura.10299':  'Concentration Aura',
  'aura.10628':  'Strength of Earth Totem',
  'aura.10627':  'Grace of Air Totem',
  'aura.16290':  'Wrath of Air Totem',
  'aura.10551':  'Windfury Totem',
  'aura.10595':  'Nature Resistance Totem',
  'aura.10596':  'Frost Resistance Totem',
  'aura.10597':  'Fire Resistance Totem',
  'aura.16615':  'Mana Spring Totem',
  'aura.16308':  'Healing Stream Totem',

  // Self-cast / proc / cooldown buffs that show up in fight tables
  'aura.12970':  'Flurry',
  'aura.20007':  'Crusader',
  'aura.23888':  'Bloodthirst',
  'aura.28777':  "Slayer's Crest",
  'aura.17528':  'Mighty Rage Potion',
  'aura.29131':  'Bloodrage',
  'aura.2457':   'Battle Stance',
  'aura.18499':  'Berserker Rage',

  // ---- Phase 2B: diagnosis panel (PRD §12) ----
  'panel.diagnosis': 'Diagnosis',
  'panel.diagnosis.loading': 'Running diagnosis…',
  'panel.diagnosis.error': 'Could not load diagnosis: {msg}',
  'diagnosis.summary.actualDps': 'Actual DPS',
  'diagnosis.summary.ruleBaseline': 'Rule baseline',
  'diagnosis.summary.ranking': 'Ranking',
  'diagnosis.summary.rankingLoading': 'Loading rankings…',
  'diagnosis.summary.percentile': '{p}th percentile',
  'diagnosis.summary.median': 'median {dps}',
  'diagnosis.summary.top10': 'top10 {dps}',
  'diagnosis.top3.title': 'Top 3 issues',
  'diagnosis.full.title': 'All findings ({count})',
  'diagnosis.empty.allGood': 'No issues found — clean run!',
  'diagnosis.empty.noFindings': 'No findings.',
  'diagnosis.drillDown': 'View raw data',
  'diagnosis.error.headline': 'Module error: {msg}',
  'diagnosis.error': 'error',

  'diagnosis.m1.name': 'Hit / Glance / Dodge',
  'diagnosis.m1.whiteMiss.headline': 'White-attack miss {pct}%',
  'diagnosis.m1.whiteMiss.detail1': 'Suggests hit gear is below the dual-wield cap (~28%).',
  'diagnosis.m1.whiteMiss.suggestion': 'Stack +hit% gear or enchants — dual-wield needs ~6-8% hit.',
  'diagnosis.m1.specialMiss.headline': 'Special-attack miss {pct}%',
  'diagnosis.m1.specialMiss.detail1': 'BT / WW / HS / Execute miss rate above the 9% cap.',
  'diagnosis.m1.specialMiss.suggestion': 'Reach 9% special-attack hit (= ~1% physical hit gear).',
  'diagnosis.m1.glance.headline': 'Glance rate {pct}%',
  'diagnosis.m1.glance.detail1': 'Weapon skill below cap — boss class +3 = 40% glance baseline.',
  'diagnosis.m1.glance.suggestion': 'Use weapon-skill enchant / racial / Edgemaster\'s Handguards to cut glances.',
  'diagnosis.m1.dodge.headline': 'Dodge rate {pct}%',
  'diagnosis.m1.dodge.detail1': 'Boss is dodging more than expected; weapon skill helps reduce dodge too.',
  'diagnosis.m1.dodge.suggestion': 'Same fix as glances: more weapon skill.',

  'diagnosis.m2.name': 'Core CD utilization',
  'diagnosis.m2.bt.headline': 'Bloodthirst {actual}/{expected} ({drift}s drift)',
  'diagnosis.m2.ww.headline': 'Whirlwind {actual}/{expected} ({drift}s drift)',
  'diagnosis.m2.suggestion': 'Press BT/WW within 0.5s of going off CD. Common causes: rage starvation, target switching, prioritising HS.',

  'diagnosis.m3.name': 'Burst CD utilization',
  'diagnosis.m3.cd.headline': 'CD used {actual}/{expected} (missed {missed})',
  'diagnosis.m3.deathWish.headline': 'Death Wish {actual}/{expected} (missed {missed})',
  'diagnosis.m3.recklessness.headline': 'Recklessness {actual}/{expected} (missed {missed})',
  'diagnosis.m3.bloodrage.headline': 'Bloodrage {actual}/{expected} (missed {missed})',
  'diagnosis.m3.suggestion': 'Use a CD-tracker WeakAura. Death Wish is 3-min CD — at most you should miss 1 use even on long fights.',

  'diagnosis.m4.name': 'HS / Cleave choice',
  'diagnosis.m4.hsTooFew.headline': 'HS+Cleave too few ({ratio}% of swings)',
  'diagnosis.m4.hsTooFew.suggestion': 'Rage-capping. Queue more HS/Cleave to dump rage between BT/WW.',
  'diagnosis.m4.hsTooMany.headline': 'HS+Cleave too many ({ratio}% of swings)',
  'diagnosis.m4.hsTooMany.suggestion': 'Eating into BT/WW rage budget. Cancel HS queue when rage drops below 50.',

  'diagnosis.m5.name': 'Rage management',
  'diagnosis.m5.bloodrage.headline': 'Bloodrage missed {missed}/{expected}',
  'diagnosis.m5.bloodrage.suggestion': 'Bloodrage is a 1-min CD — should be used every minute the fight is live.',

  'diagnosis.m6.name': 'Stance correctness',
  'diagnosis.m6.notBerserker.headline': 'Pulled in {stance} stance (not Berserker)',
  'diagnosis.m6.notBerserker.detail1': 'Berserker stance gives +3% crit. Starting in Battle/Defensive loses that for the early opener.',
  'diagnosis.m6.notBerserker.suggestion': 'Bind a stance macro and swap to Berserker in the pre-pull.',

  'diagnosis.m7.name': 'Buff coverage',
  'diagnosis.m7.missingBuff.headline': 'Missing buff: {buff} (~{impact}% DPS)',
  'diagnosis.m7.missingBuff.suggestion': 'Coordinate with raid leader / make sure consumables are reapplied after wipes.',

  'diagnosis.m8.name': 'Rotation adherence',
  'diagnosis.m8.btMaxGap.headline': 'Max BT gap: {gap}s (CD={cd}s)',
  'diagnosis.m8.wwMaxGap.headline': 'Max WW gap: {gap}s (CD={cd}s)',
  'diagnosis.m8.maxGap.headline': 'Max ability gap: {gap}s (CD={cd}s)',
  'diagnosis.m8.suggestion': 'Long gaps after a CD comes off — usually means you cast something else first, or target was unreachable.',

  // Detail-line shared labels (used across modules)
  'diagnosis.detail.attemptsMisses':    'Attempts {attempts}, misses {misses}',
  'diagnosis.detail.glancesAttempts':   'Glances {glances} of {attempts} attempts',
  'diagnosis.detail.dodgesAttempts':    'Dodges {dodges} of {attempts} attempts',
  'diagnosis.detail.usesActualExpected': 'Used {actual} / expected {expected}',
  'diagnosis.detail.driftSec':          'Total drift {sec}s',
  'diagnosis.detail.maxGapSec':         'Max gap {sec}s',
  'diagnosis.detail.firstUseSec':       'First use at {sec}s',
  'diagnosis.detail.longestGapSec':     'Longest gap {sec}s',
  'diagnosis.detail.hsCleaveCasts':     'HS {hs}, Cleave {cleave}',
  'diagnosis.detail.whiteAttempts':     'White-attack swings {n}',
  'diagnosis.detail.executePhaseSec':   'Execute phase ≈ {sec}s',

  // Buff category labels (M7 details)
  'diagnosis.buffCategory.raid':        'Raid buff',
  'diagnosis.buffCategory.world':       'World buff',
  'diagnosis.buffCategory.consumable':  'Consumable',
  'diagnosis.buffCategory.food':        'Food (well fed)',
  'diagnosis.buffCategory.self':        'Self buff',

  // Localised buff names for the M7 missing-buff headline. Curated per
  // BUFF_CATALOG entry. Renderer prefers buff.{id} → aura.{id} → enKey.
  'buff.25898': 'Greater Blessing of Kings',
  'buff.25916': 'Greater Blessing of Might',
  'buff.25894': 'Greater Blessing of Wisdom',
  'buff.25899': 'Greater Blessing of Sanctuary',
  'buff.25895': 'Greater Blessing of Salvation',
  'buff.21850': 'Gift of the Wild',
  'buff.21564': 'Prayer of Fortitude',
  'buff.27683': 'Prayer of Shadow Protection',
  'buff.20906': 'Trueshot Aura',
  'buff.25361': 'Strength of Earth Totem',
  'buff.25359': 'Grace of Air Totem',
  'buff.25587': 'Windfury Totem',
  'buff.25570': 'Mana Spring Totem',
  'buff.22888': 'Rallying Cry of the Dragonslayer',
  'buff.15366': 'Songflower Serenade',
  'buff.24425': 'Spirit of Zandalar',
  'buff.22817': "Mol'dar's Moxie",
  'buff.22818': "Fengus' Ferocity",
  'buff.22820': "Slip'kik's Savvy",
  'buff.23768': "Sayge's Dark Fortune of Damage",
  'buff.17538': 'Elixir of the Mongoose',
  'buff.17540': 'Elixir of Giants',
  'buff.16322': 'Juju Power',
  'buff.16329': 'Juju Might',
  'buff.17038': 'Winterfall Firewater',
  'buff.18192': 'Smoked Desert Dumplings',
  'buff.18194': 'Grilled Squid',
  'buff.23697': 'Blessed Sunfruit',
  'buff.18141': 'Tender Wolf Steak',
  'buff.25289': 'Battle Shout',

  'diagnosis.m9.name': 'Execute phase',
  'diagnosis.m9.lowUsage.headline': 'Execute usage {ratio}% (target {expected}, actual {actual})',
  'diagnosis.m9.lowUsage.suggestion': 'In execute phase, prioritise Execute on every GCD. Only deviate for BT off-CD if you have plenty of rage.',
  'diagnosis.m9.noExecute.headline': 'No Execute casts recorded',
  'diagnosis.m9.noExecute.suggestion': 'Was the boss killed before reaching 20%, or is Execute unbound?',

  // ---- Phase 2A: ability breakdown panel ----
  'panel.analysis': 'Combat analysis',
  'panel.analysis.loading': 'Pulling fight events from WCL…',
  'panel.analysis.loadingAll': 'Pulling events from every boss in this report…',
  'panel.analysis.error': 'Could not load analysis: {msg}',
  'panel.analysis.empty': 'No damage events for this player in this fight.',
  'panel.abilities': 'Damage by ability',
  'report.fight.all': '⚡ All bosses ({n} fights, total {dur})',
  'analysis.summary.dpsAll': 'Aggregated across {n} fights · {total} dmg over {dur}s → {dps} DPS',
  // Rage utilisation panel
  'panel.rage': 'Rage utilisation',
  'rage.summary': 'Gained {total} rage from {events} explicit events (passive procs + active CDs + potions; auto-attack rage not yet included)',
  'rage.empty': 'No explicit rage gain events. Either WCL never recorded any, or the player never pressed Bloodrage/Berserker Rage and has no rage talents.',
  'rage.col.source': 'Source',
  'rage.col.events': 'Events',
  'rage.col.total': 'Rage gained',
  'rage.col.share': 'Share',
  // Cooldown utilisation panel
  'panel.cd': 'Cooldown utilisation',
  'cd.summary': 'Pressed {uses} / {max} possible cooldown casts ({pct} overall)',
  'cd.empty': 'No tracked cooldowns were cast in this window.',
  'cd.col.ability': 'Cooldown',
  'cd.col.cd': 'CD',
  'cd.col.cdTip': 'Base cooldown of the ability.',
  'cd.col.uses': 'Uses',
  'cd.col.usesTip': 'Actual casts vs theoretical max given the analysed duration. Pre-pull use counts as the +1.',
  'cd.col.util': 'Utilisation',
  'cd.col.first': 'First',
  'cd.col.firstTip': 'Seconds from pull when the cooldown was first cast.',
  'cd.col.gap': 'Longest gap',
  'cd.col.gapTip': 'Largest gap between consecutive casts. Anything well over the base CD means the player sat on the cooldown.',
  // Debuff uptime panel
  'panel.debuffs': 'Boss debuff uptime',
  'debuff.summary': 'Raid debuffs on the primary target across {dur} of fight time',
  'debuff.empty': 'No tracked raid debuffs were applied to any target.',
  'debuff.col.name': 'Debuff',
  'debuff.col.uptime': 'Uptime',
  'debuff.col.uptimeTip': 'Total time the debuff was active divided by analysed duration. Counts any rank.',
  'debuff.col.fullStack': 'Full stacks',
  'debuff.col.fullStackTip': 'Stack-based debuffs only — Sunder Armor needs 5 stacks for full armor reduction. Time at >= threshold ÷ analysed duration.',
  'debuff.col.duration': 'Active / total',
  // Rotation deviation panel
  'panel.rotation': 'Rotation deviation',
  'rotation.summary': 'Total cooldown waste across BT + WW: {wasted}',
  'rotation.empty': 'No BT or WW casts in the analysed window.',
  'rotation.col.ability': 'Ability',
  'rotation.col.cd': 'CD',
  'rotation.col.uses': 'Uses',
  'rotation.col.wasted': 'Wasted',
  'rotation.col.wastedTip': 'Sum of (gap_between_casts − base_cd) when gap > cd. Includes idle time before the first cast. Typical causes: rage starvation, target swap, movement out of melee range.',
  'rotation.col.avg': 'Avg gap',
  'rotation.col.avgTip': 'Average time between consecutive casts. Should be close to CD if the player is on point.',
  'rotation.col.max': 'Max gap',
  'rotation.col.maxTip': 'Single longest gap between casts. Useful for spotting one-off problems (e.g. a long aggro reset).',
  'analysis.col.ability': 'Ability',
  'analysis.col.casts': 'Casts',
  'analysis.col.attempts': 'Swings',
  'analysis.col.hit': 'Hit %',
  'analysis.col.crit': 'Crit %',
  'analysis.col.miss': 'Miss',
  'analysis.col.dodge': 'Dodge',
  'analysis.col.parry': 'Parry',
  'analysis.col.glance': 'Glance',
  'analysis.col.avg': 'Avg hit',
  'analysis.col.total': 'Total dmg',
  'analysis.col.share': 'Share',
  'analysis.col.dps': 'DPS',
  'analysis.summary.dps': 'Total: {total} dmg over {dur}s → {dps} DPS',
  'analysis.col.castsTip': 'Active casts (auto-attacks have none).',
  'analysis.col.attemptsTip': 'Every event the swing rolled on the attack table — landed + misses + dodges + parries.',
  'analysis.col.hitTip': 'landed / attempts = 1 − miss%. Below ~99% means you have a hit-rating gap (DW soft cap 27%, MH-only 8%).',
  'analysis.col.critTip': 'crits / attempts (round-table %). Matches character-sheet crit when you are not crit-pushed off the table by glance.',
  'analysis.col.glanceTip': 'glances / attempts (round-table %). White-attack only — yellow abilities (BT/WW/MS) cannot glance. Mechanically fixed at 40% vs +3 boss; expect natural ±5pp variance per fight.',
  // WCL borrows the (now-removed) Word of Recall spellID 1 to mean "white melee
  // attack". We label it explicitly so users don't see "Word of Recall (OLD)".
  'ability.melee': 'Melee',
};

const ZH: Dict = {
  // App-level
  'app.title': 'RUOK 工会 · WCL 战士分析器',
  'app.subtitle': '把你 Naxx 狂暴战的实战表现与 wowsims/classic 在相同装备和天赋下的模拟结果做对比。',
  'app.author': '作者: 伟大光荣正确的 LEONIDASI',
  'app.backend': '后端',
  'app.lang.en': 'English',
  'app.lang.zh': '中文',

  // Loader
  'loader.heading': '加载战报',
  'loader.input.label': 'WCL 战报地址或编码',
  'loader.input.placeholder': 'https://vanilla.warcraftlogs.com/reports/aBcDeFgH 或 aBcDeFgH',
  'loader.button': '加载',
  'loader.loading': '正在从 WCL 拉取战报…',
  'loader.networkError': '网络错误：{msg}',

  // Report
  'report.fights': 'Naxx 首领战斗',
  'report.warriors': '本场战士',
  'report.warriors.compare': '对比战士…',
  'report.totalLength': '战报总时长',
  'report.zone': '副本',
  'report.zoneUnknown': '未知',
  'report.fight.kill': '✓ 击杀',
  'report.fight.wipe': '✗ 团灭',
  'report.fight.players': '{n} 人',
  'report.empty.fights': '本场战报里没找到 Naxxramas 首领战斗。',
  'report.empty.warriors': '本场战报里没有战士。',
  'report.loaded': '已加载 "{title}" — {fights} 场 Naxx 战斗,共 {warriors} 名战士。',

  // Player snapshot
  'player.heading.snapshot': '玩家快照:',
  'player.heading.on': '于',
  'player.fetching': '正在从 WCL 拉取 CombatantInfo…',
  'player.parsed': '解析完成 · 时长 {dur} · {result} · 天赋 {spec}',
  'player.noEvents': '该玩家在这场战斗里没有 CombatantInfo 事件。可能是中途加入,或者打日志时没开启高级战斗日志。',
  'player.result.kill': '击杀',
  'player.result.wipe': '团灭',

  // Panels
  'panel.overview': '总览',
  'panel.gear': '装备 (19 槽位)',
  'panel.auras': 'Buff 与消耗品',
  'aura.group.assumed': '团队 buff (假设 — vanilla 日志限制)',
  'assumed.heading': '假设你身上有这些团队 buff',
  'assumed.help': 'vanilla 战斗日志只 record 战斗中给的 buff，开场前给的祝福 / 野性赐福 / 坚韧祷言 等 WCL 都看不到。选阵营，勾选实际有的 buff。',
  'assumed.faction': '阵营',
  'assumed.faction.alliance': '联盟',
  'assumed.faction.horde': '部落',
  'assumed.faction.auto': '自动检测',
  'assumed.detected.alliance': '自动 → 联盟（raid 含圣骑士）',
  'assumed.detected.horde': '自动 → 部落（raid 含萨满）',
  'assumed.detected.unknown': '自动 → 未知（raid 无圣骑士/萨满）',
  'panel.talents': '天赋',
  'panel.talents.hint': '— 左键加点 / 右键减点',
  'panel.talents.openWowhead': '在 Wowhead 模拟器中打开 →',
  'panel.talents.reset': '重置',
  'panel.talents.resetTip': '恢复为推断 build',
  'panel.raw.summary': '查看原始 CombatantInfo JSON',

  // Overview / stats
  'overview.spec': '天赋',
  'overview.treePoints': '(树点数: {arms} 武器 / {fury} 狂暴 / {prot} 防护)',
  'overview.note': 'WCL 的 vanilla schema 不报告暴击/急速 — 这些会在 sim 时由敏捷、装备和天赋推算。',
  'stat.strength': '力量',
  'stat.agility': '敏捷',
  'stat.stamina': '耐力',
  'stat.intellect': '智力',
  'stat.spirit': '精神',
  'stat.armor': '护甲',
  'stat.ap': '攻强',
  'stat.crit': '暴击',
  'stat.allAttrs': '全属性',
  'assumed.group.raid': '团队 buff',
  'assumed.group.world': '世界 buff',
  'assumed.group.consumable': '消耗品',
  'assumed.group.food': '食物（饱腹）',
  'assumed.group.self': '自身 buff',
  'stat.crit.berserker': '（狂暴 +3%）',
  'stat.crit.battle': '（战斗/防御）',
  'overview.charSheetNote.base': '基础视图：种族+职业基础属性 + 装备上的属性（解析每件物品 tooltip + 永久附魔法术描述）。等同于游戏内城里看到的人物面板（无 buff）。狂暴战默认狂暴姿态 +3%。',
  'overview.charSheetNote.buffed': '战斗中视图：WCL 在开战时刻记录的属性，已包含团队 buff / 药剂 / 食物 / 世界 buff 等全部加成。等同于游戏内战斗中看到的人物面板。',
  'overview.breakdown.summary': '展开 AP / 暴击 计算细节（每一项数值的来源）',

  // 暴击拆解（hover tooltip + 展开列表）。完全由装备+附魔+天赋 计算，不使用 WCL 暴击数据。
  'crit.bd.classBase': '基础',
  'crit.bd.agility': '敏捷',
  'crit.bd.cruelty': '残忍',
  'crit.bd.stance': '姿态',
  'crit.bd.stance.berserker': '（狂暴）',
  'crit.bd.stance.none': '（无）',
  'crit.bd.gear': '装备+附魔',
  'crit.bd.gear.note': '从每件装备 tooltip 和附魔法术描述里解析',
  'crit.bd.buffs': 'Buff',
  'crit.bd.buffs.note': '勾选的世界 buff / 消耗品 / 食物 提供的暴击%',
  'crit.bd.total': '总计',

  // AP 拆解（同样的模式）
  'ap.bd.classBase': '基础',
  'ap.bd.classBase.note': '等级 × 3 − 20',
  'ap.bd.strength': '力量',
  'ap.bd.gear': '装备+附魔',
  'ap.bd.gear.note': '装备和附魔提供的平加 AP',
  'ap.bd.buffs': 'Buff',
  'ap.bd.buffs.note': '战吼 / 强效力量 / 强击光环 / Juju Might 等',
  'ap.bd.total': '总计',
  'overview.mode.base': '基础（无 buff）',
  'overview.mode.buffed': '战斗中（含 buff）',

  // Spec
  'spec.fury': '狂暴',
  'spec.arms': '武器',
  'spec.protection': '防护',

  // Gear table
  'gear.col.slot': '槽位',
  'gear.col.item': '物品',
  'gear.col.ilvl': '等级',
  'gear.col.enchants': '附魔',
  'gear.empty': '(空)',
  'gear.enchant.permanent': '永久附魔 {id}',
  'gear.enchant.temporary': '临时附魔 {id}',
  'gear.set': '套装 {id}',
  'gear.itemPrefix': '物品',

  // Slots
  'slot.Head': '头部',
  'slot.Neck': '颈部',
  'slot.Shoulder': '肩部',
  'slot.Shirt': '衬衣',
  'slot.Chest': '胸甲',
  'slot.Waist': '腰带',
  'slot.Legs': '腿甲',
  'slot.Feet': '靴子',
  'slot.Wrist': '护腕',
  'slot.Hands': '手套',
  'slot.Finger 1': '手指 1',
  'slot.Finger 2': '手指 2',
  'slot.Trinket 1': '饰品 1',
  'slot.Trinket 2': '饰品 2',
  'slot.Back': '披风',
  'slot.Main Hand': '主手',
  'slot.Off Hand': '副手',
  'slot.Ranged': '远程',
  'slot.Tabard': '战袍',

  // Talents
  'talents.allocated': '已分配',
  'talents.armsLabel': '武器 {n}',
  'talents.furyLabel': '狂暴 {n}',
  'talents.protLabel': '防护 {n}',
  'talents.total': '(总点数 {cur} / {max})',
  'talents.warn': '⚠ 与 WCL 树点数不一致',
  'talents.expected': '预期 {n} 点',
  'talents.tier': '层{n}',
  'talents.tierTitle': '第 {row} 行 (需要在该树投入 {pts} 点)',
  'talents.tree.Arms': '武器',
  'talents.tree.Fury': '狂暴',
  'talents.tree.Protection': '防护',

  // Talent names (Vanilla classic 中文译名)
  'talent.improvedHeroicStrike': '强化英勇打击',
  'talent.deflection': '招架',
  'talent.improvedRend': '强化撕裂',
  'talent.improvedCharge': '强化冲锋',
  'talent.tacticalMastery': '战术大师',
  'talent.improvedThunderClap': '强化雷霆一击',
  'talent.improvedOverpower': '强化压制',
  'talent.angerManagement': '战怒控制',
  'talent.deepWounds': '深度伤口',
  'talent.twoHandedWeaponSpecialization': '双手武器专精',
  'talent.impale': '穿刺',
  'talent.axeSpecialization': '斧类武器专精',
  'talent.sweepingStrikes': '横扫攻击',
  'talent.maceSpecialization': '锤类武器专精',
  'talent.swordSpecialization': '剑类武器专精',
  'talent.polearmSpecialization': '长柄武器专精',
  'talent.improvedHamstring': '强化断筋',
  'talent.mortalStrike': '致死打击',
  'talent.boomingVoice': '雷霆之声',
  'talent.cruelty': '残忍',
  'talent.improvedDemoralizingShout': '强化挫志怒吼',
  'talent.unbridledWrath': '无情的暴怒',
  'talent.improvedCleave': '强化顺劈斩',
  'talent.piercingHowl': '穿透怒吼',
  'talent.bloodCraze': '嗜血狂热',
  'talent.improvedBattleShout': '强化战斗怒吼',
  'talent.dualWieldSpecialization': '双武器专精',
  'talent.improvedExecute': '强化斩杀',
  'talent.enrage': '激怒',
  'talent.improvedSlam': '强化猛击',
  'talent.deathWish': '死亡之愿',
  'talent.improvedIntercept': '强化拦截',
  'talent.improvedBerserkerRage': '强化狂暴之怒',
  'talent.flurry': '武器期望', // Flurry → 武器期望 / 急速 — vanilla 译为 "武器期望"
  'talent.bloodthirst': '嗜血',
  'talent.shieldSpecialization': '盾牌专精',
  'talent.anticipation': '预感',
  'talent.improvedBloodrage': '强化血性狂暴',
  'talent.toughness': '韧性',
  'talent.ironWill': '钢铁意志',
  'talent.lastStand': '破釜沉舟',
  'talent.improvedShieldBlock': '强化盾牌格挡',
  'talent.improvedRevenge': '强化复仇',
  'talent.defiance': '反抗',
  'talent.improvedSunderArmor': '强化破甲',
  'talent.improvedDisarm': '强化缴械',
  'talent.improvedTaunt': '强化嘲讽',
  'talent.improvedShieldWall': '强化盾墙',
  'talent.concussionBlow': '震荡之击',
  'talent.improvedShieldBash': '强化盾击',
  'talent.oneHandedWeaponSpecialization': '单手武器专精',
  'talent.shieldSlam': '盾牌猛击',

  // Naxx-era raid buffs (Vanilla classic 中文名)
  'aura.2458':   '狂暴姿态',
  'aura.11348':  '强效护甲药水',
  'aura.22817':  '芬古斯的凶残',
  'aura.22818':  '莫达的勇气',
  'aura.22820':  '斯里基克的智慧',
  'aura.17038':  '冬泉火酒',
  'aura.24799':  '营养充沛',
  'aura.25804':  '朗姆斯朗姆酒——黑标',
  'aura.17538':  '猫鼬药剂',
  'aura.26276':  '强效火力',
  'aura.17539':  '强效奥术合剂',
  'aura.16323':  '力量巫毒',
  'aura.16329':  '强效巫毒',
  'aura.3593':   '坚韧药剂',
  'aura.355363': '屠龙者的呐喊',
  'aura.355366': '酋长的祝福',
  'aura.355365': '赞达拉的精神',
  'aura.15366':  '歌之花小夜曲',
  'aura.17626':  '泰坦合剂',
  'aura.24382':  '赞扎的精魂',
  'aura.24361':  '强效巨魔之血药剂',

  // Naxx boss 中文名 — 用 wowhead 中文站官方译名
  'boss.51107': '阿努布雷坎',
  'boss.51110': '黑女巫法琳娜',
  'boss.51116': '迈克斯纳',
  'boss.51117': '瘟疫使者诺斯',
  'boss.51112': '肮脏的希尔盖',
  'boss.51115': '洛欧塞布',
  'boss.51113': '教官拉苏维奥斯',
  'boss.51109': '收割者戈提克',
  'boss.51121': '四骑士',
  'boss.51118': '帕奇维克',
  'boss.51111': '格罗布鲁斯',
  'boss.51108': '格拉斯',
  'boss.51120': '塔迪乌斯',
  'boss.51119': '萨菲隆',
  'boss.51114': '克尔苏加德',
  // Classic (WotLK) 同名
  'boss.1107': '阿努布雷坎',
  'boss.1110': '黑女巫法琳娜',
  'boss.1116': '迈克斯纳',
  'boss.1117': '瘟疫使者诺斯',
  'boss.1112': '肮脏的希尔盖',
  'boss.1115': '洛欧塞布',
  'boss.1113': '教官拉苏维奥斯',
  'boss.1109': '收割者戈提克',
  'boss.1121': '四骑士',
  'boss.1118': '帕奇维克',
  'boss.1111': '格罗布鲁斯',
  'boss.1108': '格拉斯',
  'boss.1120': '塔迪乌斯',
  'boss.1119': '萨菲隆',
  'boss.1114': '克尔苏加德',

  // 分组标题
  'aura.group.starting':   '开战快照（自身 buff）',
  'aura.group.persistent': '团队 buff（≥70% 时长）',
  'aura.group.transient':  '{n} 个临时 buff（触发 / 冷却）',

  // 常见团队 buff（不在 COMBATANT_INFO 里）
  'aura.25898':  '强效王者祝福',
  'aura.25899':  '强效庇护祝福',
  'aura.25895':  '强效力量祝福',
  'aura.25894':  '强效智慧祝福',
  'aura.25890':  '强效光明祝福',
  'aura.25918':  '强效拯救祝福',
  'aura.20217':  '王者祝福',
  'aura.19742':  '智慧祝福',
  'aura.19740':  '力量祝福',
  'aura.20911':  '庇护祝福',
  'aura.1038':   '拯救祝福',
  'aura.10938':  '真言术：韧',
  'aura.21564':  '坚韧祷言',
  'aura.27683':  '暗影防护祷言',
  'aura.10958':  '暗影防护',
  'aura.21850':  '野性赐福',
  'aura.9885':   '野性印记',
  'aura.27007':  '强击光环',
  'aura.20140':  '强效智慧祝福',
  'aura.25289':  '战斗怒吼',
  'aura.469':    '命令怒吼',
  'aura.27149':  '虔诚光环',
  'aura.27150':  '惩戒光环',
  'aura.10299':  '专注光环',
  'aura.10628':  '大地之力图腾',
  'aura.10627':  '风之优雅图腾',
  'aura.16290':  '风怒图腾',
  'aura.10551':  '风怒图腾',
  'aura.10595':  '自然抗性图腾',
  'aura.10596':  '冰霜抗性图腾',
  'aura.10597':  '火焰抗性图腾',
  'aura.16615':  '法力之泉图腾',
  'aura.16308':  '治疗之泉图腾',

  // 战斗内自施 / proc / 冷却 buff
  'aura.12970':  '连击',
  'aura.20007':  '十字军',
  'aura.23888':  '嗜血触发',
  'aura.28777':  '屠杀者纹章',
  'aura.17528':  '狂怒药水',
  'aura.29131':  '血性狂暴',
  'aura.2457':   '战斗姿态',
  'aura.18499':  '狂暴之怒',

  // ---- Phase 2B: 诊断面板（PRD §12）----
  // 中文文案占位，待 PM 校对（PRD §17.5）
  'panel.diagnosis': '诊断报告',
  'panel.diagnosis.loading': '正在生成诊断报告…',
  'panel.diagnosis.error': '诊断生成失败：{msg}',
  'diagnosis.summary.actualDps': '实际 DPS',
  'diagnosis.summary.ruleBaseline': '规则上限',
  'diagnosis.summary.ranking': '团内排名',
  'diagnosis.summary.rankingLoading': '正在加载排名…',
  'diagnosis.summary.percentile': '第 {p} 百分位',
  'diagnosis.summary.median': '中位 {dps}',
  'diagnosis.summary.top10': 'top10 {dps}',
  'diagnosis.top3.title': '主要问题（Top 3）',
  'diagnosis.full.title': '全部诊断（{count}）',
  'diagnosis.empty.allGood': '没发现问题——干净的一场！',
  'diagnosis.empty.noFindings': '无诊断结果。',
  'diagnosis.drillDown': '查看原始数据',
  'diagnosis.error.headline': '模块出错：{msg}',
  'diagnosis.error': '错误',

  'diagnosis.m1.name': '命中 / 偏斜 / 闪避',
  'diagnosis.m1.whiteMiss.headline': '白字未命中 {pct}%',
  'diagnosis.m1.whiteMiss.detail1': '命中等级低于双持上限（约 28%）',
  'diagnosis.m1.whiteMiss.suggestion': '叠 +命中% 装备 / 附魔 — 双持需要约 6-8% 命中。',
  'diagnosis.m1.specialMiss.headline': '特殊招未命中 {pct}%',
  'diagnosis.m1.specialMiss.detail1': 'BT / WW / HS / 斩杀 命中率低于 9% 上限',
  'diagnosis.m1.specialMiss.suggestion': '凑齐 9% 特殊招命中（约 1% 物理命中等级）。',
  'diagnosis.m1.glance.headline': '偏斜率 {pct}%',
  'diagnosis.m1.glance.detail1': '武器熟练不足，对 +3 boss 默认偏斜 40%',
  'diagnosis.m1.glance.suggestion': '武器技能附魔 / 种族特长 / 大师之手套，可以把偏斜降到 10-15%。',
  'diagnosis.m1.dodge.headline': '被闪避 {pct}%',
  'diagnosis.m1.dodge.detail1': 'Boss 闪避率偏高，武器熟练同样能降低被闪避',
  'diagnosis.m1.dodge.suggestion': '解决方法和偏斜一样：增加武器熟练。',

  'diagnosis.m2.name': '核心 CD 利用率',
  'diagnosis.m2.bt.headline': '嗜血 {actual}/{expected}（漂移 {drift} 秒）',
  'diagnosis.m2.ww.headline': '旋风斩 {actual}/{expected}（漂移 {drift} 秒）',
  'diagnosis.m2.suggestion': 'BT/WW 亮起 0.5 秒内打掉。常见原因：怒气不足、切目标、优先打 HS。',

  'diagnosis.m3.name': '爆发 CD 利用率',
  'diagnosis.m3.cd.headline': 'CD 使用 {actual}/{expected}（漏 {missed} 次）',
  'diagnosis.m3.deathWish.headline': '死亡之愿 {actual}/{expected}（漏 {missed} 次）',
  'diagnosis.m3.recklessness.headline': '鲁莽 {actual}/{expected}（漏 {missed} 次）',
  'diagnosis.m3.bloodrage.headline': '血性狂暴 {actual}/{expected}（漏 {missed} 次）',
  'diagnosis.m3.suggestion': '用 WeakAura 提醒 CD ready。死亡之愿 3 分钟 CD，长战最多漏 1 次是合理的。',

  'diagnosis.m4.name': 'HS / 顺劈选择',
  'diagnosis.m4.hsTooFew.headline': 'HS+顺劈太少（占挥击 {ratio}%）',
  'diagnosis.m4.hsTooFew.suggestion': '怒气溢出。在 BT/WW 之间多 queue 几次 HS/顺劈倾泻怒气。',
  'diagnosis.m4.hsTooMany.headline': 'HS+顺劈太多（占挥击 {ratio}%）',
  'diagnosis.m4.hsTooMany.suggestion': '吃掉了 BT/WW 的怒气预算。怒气低于 50 时取消 HS queue。',

  'diagnosis.m5.name': '怒气管理',
  'diagnosis.m5.bloodrage.headline': '血性狂暴漏 {missed}/{expected} 次',
  'diagnosis.m5.bloodrage.suggestion': '血性狂暴 1 分钟 CD — 战斗每分钟应该都用一次。',

  'diagnosis.m6.name': '姿态正确性',
  'diagnosis.m6.notBerserker.headline': '开战时不在狂暴姿态（在 {stance} 姿态）',
  'diagnosis.m6.notBerserker.detail1': '狂暴姿态 +3% 暴击。开局在战斗/防御姿态损失了起手的 +3%。',
  'diagnosis.m6.notBerserker.suggestion': '绑一个姿态切换宏，开怪前切到狂暴姿态。',

  'diagnosis.m7.name': 'Buff 覆盖',
  'diagnosis.m7.missingBuff.headline': '缺 buff: {buff}（约 -{impact}% DPS）',
  'diagnosis.m7.missingBuff.suggestion': '和团长协调；灭团后记得重新上消耗品。',

  'diagnosis.m8.name': '输出循环遵循度',
  'diagnosis.m8.btMaxGap.headline': '嗜血最大空窗 {gap} 秒（CD={cd} 秒）',
  'diagnosis.m8.wwMaxGap.headline': '旋风斩最大空窗 {gap} 秒（CD={cd} 秒）',
  'diagnosis.m8.maxGap.headline': '技能最大空窗 {gap} 秒（CD={cd} 秒）',
  'diagnosis.m8.suggestion': 'CD 好后长时间没用，通常是先打了别的，或目标不在范围内。',

  // 跨模块共用的详情标签
  'diagnosis.detail.attemptsMisses':    '尝试 {attempts} 次，未命中 {misses} 次',
  'diagnosis.detail.glancesAttempts':   '偏斜 {glances} / {attempts} 次',
  'diagnosis.detail.dodgesAttempts':    '被闪避 {dodges} / {attempts} 次',
  'diagnosis.detail.usesActualExpected': '实际使用 {actual} / 期望 {expected}',
  'diagnosis.detail.driftSec':          '累计漂移 {sec} 秒',
  'diagnosis.detail.maxGapSec':         '最大空窗 {sec} 秒',
  'diagnosis.detail.firstUseSec':       '首次使用 {sec} 秒',
  'diagnosis.detail.longestGapSec':     '最长空窗 {sec} 秒',
  'diagnosis.detail.hsCleaveCasts':     '英勇打击 {hs} 次，顺劈 {cleave} 次',
  'diagnosis.detail.whiteAttempts':     '白字挥击 {n} 次',
  'diagnosis.detail.executePhaseSec':   '斩杀阶段约 {sec} 秒',

  // Buff 分类标签（M7 详情）
  'diagnosis.buffCategory.raid':        '团队 buff',
  'diagnosis.buffCategory.world':       '世界 buff',
  'diagnosis.buffCategory.consumable':  '消耗品',
  'diagnosis.buffCategory.food':        '食物（饱腹）',
  'diagnosis.buffCategory.self':        '自身 buff',

  // 中文 buff 名（M7 missing-buff 标题专用）。renderer 优先 buff.{id}
  // → aura.{id} → enKey 兜底。
  'buff.25898': '强效王者祝福',
  'buff.25916': '强效力量祝福',
  'buff.25894': '强效智慧祝福',
  'buff.25899': '强效庇护祝福',
  'buff.25895': '强效拯救祝福',
  'buff.21850': '野性赐福',
  'buff.21564': '坚韧祷言',
  'buff.27683': '暗影防护祷言',
  'buff.20906': '强击光环',
  'buff.25361': '大地之力图腾',
  'buff.25359': '凌风图腾',
  'buff.25587': '风怒图腾',
  'buff.25570': '法力之泉图腾',
  'buff.22888': '屠龙者的怒吼',
  'buff.15366': '歌之花小夜曲',
  'buff.24425': '赞达拉之灵',
  'buff.22817': '莫尔达的勇气',
  'buff.22818': '芬古斯的凶猛',
  'buff.22820': '斯里克尔的精明',
  'buff.23768': '塞古斯的暗运·伤害',
  'buff.17538': '猫鼬药剂',
  'buff.17540': '巨人药剂',
  'buff.16322': '巨魔之力',
  'buff.16329': '强效巫毒',
  'buff.17038': '冬泉火酒',
  'buff.18192': '烟熏沙漠饺',
  'buff.18194': '烤鱿鱼',
  'buff.23697': '受祝福的阳桃',
  'buff.18141': '嫩狼肉排',
  'buff.25289': '战斗怒吼',

  'diagnosis.m9.name': '斩杀阶段',
  'diagnosis.m9.lowUsage.headline': '斩杀使用率 {ratio}%（期望 {expected}、实际 {actual}）',
  'diagnosis.m9.lowUsage.suggestion': '20% 后每个 GCD 优先按 Execute。除非怒气充裕且 BT 立即可用，否则不要插 BT。',
  'diagnosis.m9.noExecute.headline': '没有斩杀施法记录',
  'diagnosis.m9.noExecute.suggestion': 'boss 在 20% 之前被击杀？还是斩杀没绑键？',

  // ---- Phase 2A: 战斗分析面板 ----
  'panel.analysis': '战斗分析',
  'panel.analysis.loading': '正在从 WCL 拉取战斗事件…',
  'panel.analysis.loadingAll': '正在拉取本场战报所有 Boss 战的事件…',
  'panel.analysis.error': '加载分析失败：{msg}',
  'panel.analysis.empty': '该玩家在这场战斗里没有伤害事件。',
  'panel.abilities': '技能伤害拆解',
  'report.fight.all': '⚡ 全部 Boss（合并 {n} 场，共 {dur}）',
  'analysis.summary.dpsAll': '合计 {n} 场 · {total} 伤害 / {dur}秒 → {dps} DPS',
  // 怒气利用面板
  'panel.rage': '怒气利用',
  'rage.summary': '本场获得 {total} 怒气，共 {events} 个事件（被动触发 + 主动冷却 + 药水；自动攻击产生的怒气暂未计入）',
  'rage.empty': '没有显式怒气事件。要么 WCL 没记录，要么玩家没用过血性狂暴 / 狂暴之怒，且没有怒气类天赋。',
  'rage.col.source': '来源',
  'rage.col.events': '次数',
  'rage.col.total': '获得怒气',
  'rage.col.share': '占比',
  // 冷却利用率
  'panel.cd': '冷却利用率',
  'cd.summary': '共按下 {uses} / {max} 次冷却 ({pct} 整体利用率)',
  'cd.empty': '本场没有可跟踪的冷却被使用。',
  'cd.col.ability': '冷却',
  'cd.col.cd': '间隔',
  'cd.col.cdTip': '该技能的基础冷却时间。',
  'cd.col.uses': '次数',
  'cd.col.usesTip': '实际使用次数 vs 该时长允许的理论最大次数。开战前预读 + 1 算在内。',
  'cd.col.util': '利用率',
  'cd.col.first': '首次',
  'cd.col.firstTip': '相对开战的秒数 — 第一次按下该冷却的时刻。',
  'cd.col.gap': '最长间隔',
  'cd.col.gapTip': '相邻两次施放之间的最长间隔。明显超过基础冷却 = 玩家压住没用。',
  // 减益覆盖率
  'panel.debuffs': 'Boss 减益覆盖率',
  'debuff.summary': '战斗时长 {dur} 期间，主目标上的团队减益覆盖情况',
  'debuff.empty': '没有跟踪到任何减益施加在目标上。',
  'debuff.col.name': '减益',
  'debuff.col.uptime': '覆盖率',
  'debuff.col.uptimeTip': '减益生效总时长 ÷ 分析时长。任意等级都计入。',
  'debuff.col.fullStack': '满层',
  'debuff.col.fullStackTip': '仅叠层类减益。破甲需 5 层达到最大护甲减免。等级 ≥ 阈值的总时长 ÷ 分析时长。',
  'debuff.col.duration': '生效 / 总时长',
  // 循环偏差
  'panel.rotation': '循环偏差',
  'rotation.summary': 'BT + WW 总冷却浪费时间：{wasted}',
  'rotation.empty': '本次窗口内没有 BT 或 WW 的施放。',
  'rotation.col.ability': '技能',
  'rotation.col.cd': '冷却',
  'rotation.col.uses': '次数',
  'rotation.col.wasted': '浪费',
  'rotation.col.wastedTip': '相邻两次施放间隔超过基础冷却的总和（含首次施放前的空闲时间）。常见原因：怒气不足、切换目标、跑位脱离近战范围。',
  'rotation.col.avg': '平均间隔',
  'rotation.col.avgTip': '相邻两次施放的平均间隔。打得好的话应接近 CD。',
  'rotation.col.max': '最长间隔',
  'rotation.col.maxTip': '单次最长间隔。用来抓个别异常（比如一次掉落仇恨重新拉）。',
  'analysis.col.ability': '技能',
  'analysis.col.casts': '施放',
  'analysis.col.attempts': '挥击',
  'analysis.col.hit': '命中率',
  'analysis.col.crit': '暴击率',
  'analysis.col.miss': '未命中',
  'analysis.col.dodge': '闪避',
  'analysis.col.parry': '招架',
  'analysis.col.glance': '偏斜',
  'analysis.col.avg': '平均伤害',
  'analysis.col.total': '总伤害',
  'analysis.col.share': '占比',
  'analysis.col.dps': 'DPS',
  'analysis.summary.dps': '合计：{total} 伤害 / {dur}秒 → {dps} DPS',
  'analysis.col.castsTip': '主动施放次数（自动攻击没有施放事件）。',
  'analysis.col.attemptsTip': '上攻击表的所有事件 — 造成伤害的击中 + 未命中 + 闪避 + 招架。',
  'analysis.col.hitTip': '造成伤害的事件 / 挥击数 = 1 − 未命中率。明显低于 99% 说明命中等级不够（双武器软上限 27%，单武器 8%）。',
  'analysis.col.critTip': '暴击数 / 总挥击数（圆桌占比）。除非被偏斜挤出表外，否则跟人物面板上的暴击率一致。',
  'analysis.col.glanceTip': '偏斜数 / 总挥击数（圆桌占比）。仅白刃攻击会偏斜，黄字技能（嗜血/旋风斩/致死打击）不会。机制固定：对 +3 Boss 是 40%，单场 ±5pp 浮动属正常。',
  'ability.melee': '近战攻击',
};

const DICTS: Record<Lang, Dict> = { en: EN, zh: ZH };

export function detectLang(): Lang {
  // URL override always wins, e.g. ?lang=zh forces Chinese and rewrites the
  // stored preference. Useful for users whose localStorage got pinned to en.
  if (typeof location !== 'undefined') {
    const params = new URLSearchParams(location.search);
    const q = params.get('lang');
    if (q === 'en' || q === 'zh') {
      try { localStorage.setItem(STORAGE_KEY, q); } catch { /* ignore */ }
      return q;
    }
  }
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'zh') return stored;
  }
  if (typeof navigator !== 'undefined') {
    if (navigator.language && navigator.language.toLowerCase().startsWith('zh')) return 'zh';
  }
  return 'en';
}

export function getLang(): Lang {
  return currentLang;
}

export function setLang(lang: Lang, persist = true): void {
  currentLang = lang;
  if (persist && typeof localStorage !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, lang);
  }
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lang === 'zh' ? 'zh-CN' : 'en';
  }
  for (const fn of listeners) fn();
}

export function onLangChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function t(key: string, vars?: Record<string, string | number>): string {
  const dict = DICTS[currentLang];
  const fallback = DICTS.en;
  let template = dict[key];
  if (template === undefined) template = fallback[key];
  if (template === undefined) return key;
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, k) => {
    const v = vars[k];
    return v === undefined ? `{${k}}` : String(v);
  });
}

// Convenience: returns the slot's translated label given the canonical English slot name.
export function tSlot(slotEnglish: string): string {
  return t(`slot.${slotEnglish}`);
}

// Convenience: returns the talent fieldName's translated label.
export function tTalent(fieldName: string): string {
  return t(`talent.${fieldName}`);
}

// Returns translated aura name; falls back to the WCL-provided English name
// when we don't have a translation for that spell id.
export function tAuraName(abilityId: number, fallback: string): string {
  const dict = DICTS[currentLang];
  const en = DICTS.en;
  const key = `aura.${abilityId}`;
  return dict[key] ?? en[key] ?? fallback;
}

// Translated boss / encounter name by WCL encounterID. In Chinese mode the
// English name is appended in parens for cross-reference. Falls back to the
// WCL-provided English name when we don't have a translation.
export function tBossName(encounterID: number, fallback: string): string {
  const key = `boss.${encounterID}`;
  const en = DICTS.en[key] ?? fallback;
  if (currentLang === 'zh') {
    const zh = DICTS.zh[key];
    if (zh && zh !== en) return `${zh}（${en}）`;
  }
  return en;
}

// ---- Wowhead localisation ----
// All wowhead links are routed through our /api/wh reverse-proxy so users in
// ZAM Network's geo-blocked regions still reach the page. Vanilla classic
// Chinese pages live under /classic/cn/<type>=<id>; English under /classic/.
// (cn.wowhead.com 301-redirects, the proxy follows redirects for us.)

export function wowheadUrl(kind: 'item' | 'spell', id: number | string): string {
  const localePath = getLang() === 'zh' ? 'classic/cn' : 'classic';
  return `/api/wh/${localePath}/${kind}=${id}`;
}

// data-wowhead string. domain=cn signals the Power widget to request the
// Chinese tooltip blob (so the popup is in Chinese as well as the link text).
export function wowheadDataAttr(kind: 'item' | 'spell', id: number | string): string {
  const prefix = getLang() === 'zh' ? 'domain=cn&' : '';
  return `${prefix}${kind}=${id}`;
}
